/**
 * Initializes an Alpine.js data object to manage a parallax type effect for a web page section with multiple blocks.
 * Key features include:
 * 1. Dynamic Positioning: Calculates positions of content blocks and adjusts based on scroll and resize events.
 * 2. Scroll Handling: Implements throttle on scroll events to optimize performance and recalculates block visibility.
 * 3. Sticky Content Management: Adjusts content opacity and stickiness based on the visibility within the viewport.
 * 4. Responsive Updates: Recalculates positions on window resize to maintain the parallax effect across devices.
 */

function parallaxSection(stickyContent) {
  return {
    blocks: [],
    throttleTimer: false,

    eventThrottle(callback) {
      if (this.throttleTimer) return;
      this.throttleTimer = true;
      requestAnimationFrame(() => {
        callback();
        this.throttleTimer = false;
      });
    },

    init() {
      this.calculatePositions()
      setTimeout(() => this.calculatePositions(), 1500);
      window.addEventListener('resize', () => this.eventThrottle(() => this.calculatePositions()));
      window.addEventListener('scroll', () => this.eventThrottle(() => this.handleScroll()));
    },

    calculateBlocksPositions() {
      const blocks = this.$el.querySelectorAll('.parallax-block-wrapper');
      const section = this.$el;
      const sectionHeight = section.offsetHeight;
      const blockHeight = sectionHeight / blocks.length;
      this.blocks = Array.from(blocks).map((block, index) => {
        const rect = block.getBoundingClientRect();
        const rectSection = section.getBoundingClientRect();
        return {
          element: block,
          top: window.scrollY + rectSection.top + blockHeight * index,
          height: blockHeight,
        };
      });
    },

    setContentPositions() {
      const contentBlock = this.$el.querySelector('.parallax-sticky-elements');
      const firstContentBlock = this.$el.querySelector('.block-section-content--0');
      const windowHeight = window.innerHeight;
      let windowWidth = window.innerWidth;
      let bottomMargin = windowWidth > 1023 ? 84 : 70;
      if (firstContentBlock) {
        contentBlock.style.paddingTop = `${firstContentBlock.offsetHeight + 32}px`;
      }
      if (contentBlock) {
        const blockHeight = contentBlock.offsetHeight;
        const topPosition = windowHeight - blockHeight;
        contentBlock.style.top = `${topPosition - bottomMargin}px`;
      }
    },

    calculatePositions() {
      this.calculateBlocksPositions();
      this.setContentPositions();
    },

    handleScroll() {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const bottomScrollPosition = scrollPosition + windowHeight;
      const section = this.$el;
      const sectionTop = window.scrollY + section.getBoundingClientRect().top;
      const sectionBottom = sectionTop + section.offsetHeight;
      const lastBlockTop = this.blocks[this.blocks.length - 1].top;

      let sectionScrollPercentage;
      if (scrollPosition <= sectionTop) {
        sectionScrollPercentage = 0;
      } else if (scrollPosition >= sectionBottom - windowHeight) {
        sectionScrollPercentage = 100;
      } else {
        const scrolledThrough = scrollPosition - sectionTop;
        const totalScrollable = sectionBottom - sectionTop - windowHeight;
        sectionScrollPercentage = (scrolledThrough / totalScrollable) * 100;
      }

      const parallaxNavTopStyle = sectionScrollPercentage * 0.9;

      const parallaxNavLocation = this.$el.querySelector('.parallax-nav-location');
      if (parallaxNavLocation) {
        parallaxNavLocation.style.top = `${parallaxNavTopStyle}%`;
      }

      this.blocks.forEach((block, index) => {
        const blockEnd = block.top + block.height;
        const visibleStart = Math.max(scrollPosition, block.top);
        const visibleEnd = Math.min(blockEnd, bottomScrollPosition);
        const visibleHeight = Math.max(0, visibleEnd - visibleStart);
        const visibilityPercentage = (visibleHeight / block.height) * 100;
        const stickyZoneBottom = lastBlockTop;
        if(stickyContent) {
          const contentBlock = this.$el.querySelector(`.block-section-content--${index}`);
          let opacity = Math.max(0, Math.min(1, (visibilityPercentage - 51) / 29));
          contentBlock.style.opacity = opacity;
        }
        if (scrollPosition >= block.top && scrollPosition < stickyZoneBottom) {
          block.element.classList.add('sticky-block');
        } else {
          block.element.classList.remove('sticky-block');
        }
      });
    },

    toNextBlock(currentIndex) {
      const nextIndex = currentIndex === this.blocks.length - 1 ? 0 : currentIndex + 1;
      const nextBlockTop = this.blocks[nextIndex].top;

      window.scrollTo(0, nextBlockTop, 10000);
    },
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('parallaxSection', parallaxSection);
});
